import React, { useState } from 'react';
import { X, Upload, FileText, CheckCircle2, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import BookingModal from './BookingModal';

export const StrategySessionForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    revenue: '',
    currentChallenges: '',
    timeframe: '',
    avgJobValue: '1200'
  });
  const [callLogsFile, setCallLogsFile] = useState(null);
  const [audit, setAudit] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [strategyId, setStrategyId] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Only .csv files are supported.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File must be under 10 MB.');
      return;
    }
    setError('');
    setCallLogsFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      let response;
      if (callLogsFile) {
        const fd = new FormData();
        Object.entries(formData).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') fd.append(k, v);
        });
        fd.append('callLogs', callLogsFile);
        response = await fetch(`${apiUrl}/api/strategy-session/submit-with-logs`, {
          method: 'POST',
          body: fd,
        });
      } else {
        // /submit endpoint doesn't accept avgJobValue; strip it
        const { avgJobValue: _omit, ...rest } = formData;
        response = await fetch(`${apiUrl}/api/strategy-session/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(rest),
        });
      }

      if (response.ok) {
        const data = await response.json();
        if (data?.audit) setAudit(data.audit);
        if (data?.id) setStrategyId(data.id);
        setSubmitted(true);
      } else {
        const errBody = await response.json().catch(() => ({}));
        setError(errBody.detail || 'Submission failed. Try again.');
      }
    } catch (err) {
      setError(err.message || 'Submission failed. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl glass rounded-2xl p-8 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {!submitted ? (
          <>
            <h2 className="text-3xl font-headline font-bold text-[#F4F4F5] mb-2">
              Book Your Strategy Session
            </h2>
            <p className="text-[#A1A1AA] font-body mb-6">
              Tell us about your business and we will get back to you within 24 hours to schedule your session.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Annual Revenue Range *
                </label>
                <select
                  required
                  value={formData.revenue}
                  onChange={(e) => setFormData({...formData, revenue: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                >
                  <option value="">Select range</option>
                  <option value="under-1m">Under $1M</option>
                  <option value="1m-5m">$1M - $5M</option>
                  <option value="5m-10m">$5M - $10M</option>
                  <option value="10m-50m">$10M - $50M</option>
                  <option value="50m-plus">$50M+</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  What are your biggest operational challenges? *
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.currentChallenges}
                  onChange={(e) => setFormData({...formData, currentChallenges: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599] resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  When are you looking to start? *
                </label>
                <select
                  required
                  value={formData.timeframe}
                  onChange={(e) => setFormData({...formData, timeframe: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                >
                  <option value="">Select timeframe</option>
                  <option value="immediately">Immediately</option>
                  <option value="1-2-weeks">1-2 weeks</option>
                  <option value="1-month">Within 1 month</option>
                  <option value="2-3-months">2-3 months</option>
                  <option value="exploring">Just exploring</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Average job value <span className="text-[#A1A1AA] font-normal">(used for your missed-revenue projection)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] font-body">$</span>
                  <input
                    type="number"
                    min="50"
                    step="50"
                    value={formData.avgJobValue}
                    onChange={(e) => setFormData({ ...formData, avgJobValue: e.target.value })}
                    className="w-full pl-8 pr-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                    placeholder="1200"
                    data-testid="strategy-avg-job-value-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Upload your call logs <span className="text-[#A1A1AA] font-normal">(optional CSV — we'll pre-compute your missed-revenue audit)</span>
                </label>
                <label
                  htmlFor="strategy-call-logs-input"
                  className={`flex items-center gap-3 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    callLogsFile
                      ? 'border-[#00E599] bg-[#00E599]/10'
                      : 'border-white/10 hover:border-[#00E599]/50 bg-[#0F0F13]'
                  }`}
                  data-testid="strategy-call-logs-dropzone"
                >
                  {callLogsFile ? (
                    <>
                      <FileText className="w-5 h-5 text-[#00E599]" />
                      <span className="text-sm text-[#F4F4F5] font-body truncate flex-1">
                        {callLogsFile.name}{' '}
                        <span className="text-[#A1A1AA]">
                          ({(callLogsFile.size / 1024).toFixed(1)} KB)
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setCallLogsFile(null);
                        }}
                        className="text-xs text-[#A1A1AA] hover:text-red-400"
                        data-testid="strategy-call-logs-remove"
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-[#A1A1AA]" />
                      <span className="text-sm text-[#A1A1AA] font-body">
                        Drop a CSV here or click to browse (max 10 MB)
                      </span>
                    </>
                  )}
                  <input
                    id="strategy-call-logs-input"
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={handleFileChange}
                    data-testid="strategy-call-logs-input"
                  />
                </label>
              </div>

              {error && (
                <div className="text-sm text-red-400 font-body" data-testid="strategy-form-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#00E599] hover:bg-[#00C985] text-[#050505] px-8 py-4 text-base font-body font-bold transition-all duration-300 hover:shadow-xl hover:shadow-[#00E599]/30 rounded-lg"
              >
                {isSubmitting ? 'Submitting...' : 'Request Strategy Session'}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-8" data-testid="strategy-form-success">
            <div className="w-16 h-16 bg-[#00E599] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-[#050505]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-headline font-bold text-[#F4F4F5] mb-2">
              Request Received!
            </h3>
            <p className="text-[#A1A1AA] font-body mb-6">
              We will review your information and contact you within 24 hours to schedule your strategy session.
            </p>

            {audit && (
              <div className="glass p-6 rounded-xl border border-[#00E599]/30 text-left" data-testid="strategy-audit-preview">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 className="w-5 h-5 text-[#00E599]" />
                  <h4 className="text-lg font-headline font-bold text-[#F4F4F5]">Preliminary Audit</h4>
                </div>

                {audit.projected_monthly_missed_revenue ? (
                  <div className="mb-5 p-5 rounded-lg bg-[#050505] border border-red-500/20">
                    <div className="text-xs text-[#A1A1AA] font-body uppercase tracking-wider mb-1">
                      Estimated monthly missed revenue
                    </div>
                    <div
                      className="text-4xl sm:text-5xl font-headline font-bold text-red-400"
                      data-testid="audit-monthly-revenue"
                    >
                      ${audit.projected_monthly_missed_revenue.toLocaleString()}
                    </div>
                    {audit.projected_annual_missed_revenue && (
                      <div className="text-sm text-[#A1A1AA] font-body mt-1">
                        ~${audit.projected_annual_missed_revenue.toLocaleString()} / year at current pace
                      </div>
                    )}
                  </div>
                ) : audit.missed_revenue_in_file ? (
                  <div className="mb-5 p-5 rounded-lg bg-[#050505] border border-red-500/20">
                    <div className="text-xs text-[#A1A1AA] font-body uppercase tracking-wider mb-1">
                      Missed revenue in this file
                    </div>
                    <div
                      className="text-4xl sm:text-5xl font-headline font-bold text-red-400"
                      data-testid="audit-file-revenue"
                    >
                      ${audit.missed_revenue_in_file.toLocaleString()}
                    </div>
                  </div>
                ) : null}

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-[#A1A1AA] font-body">Calls in file</div>
                    <div className="text-2xl font-headline font-bold gradient-text" data-testid="audit-row-count">
                      {audit.row_count}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[#A1A1AA] font-body">Estimated missed</div>
                    <div className="text-2xl font-headline font-bold text-red-400" data-testid="audit-missed-count">
                      {audit.estimated_missed}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[#A1A1AA] font-body">Days covered</div>
                    <div className="text-2xl font-headline font-bold text-[#F4F4F5]" data-testid="audit-days-covered">
                      {audit.days_covered ?? '—'}
                    </div>
                  </div>
                </div>

                <p className="text-xs text-[#A1A1AA] font-body">
                  Projection assumes a <span className="text-[#00E599] font-semibold">67% close rate</span> on fast-answered calls (industry benchmark). We'll refine these numbers on your live strategy call.
                </p>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    onClick={() => setIsBookingOpen(true)}
                    data-testid="audit-book-call-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 whitespace-nowrap bg-[#00E599] hover:bg-[#00C985] text-[#050505] px-4 sm:px-6 py-4 text-sm sm:text-base font-body font-bold transition-all duration-300 hover:shadow-xl hover:shadow-[#00E599]/30 rounded-lg"
                  >
                    <Calendar className="w-5 h-5 shrink-0" />
                    Book Strategy Call
                  </Button>
                  <a
                    href="/ongoing-support"
                    data-testid="audit-skip-to-plans-btn"
                    className="flex-1 inline-flex items-center justify-center whitespace-nowrap px-4 sm:px-6 py-4 text-sm sm:text-base font-body font-semibold text-[#F4F4F5] border border-white/10 hover:border-[#00E599]/40 rounded-lg transition-colors"
                  >
                    Skip — pick a plan
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <BookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        defaults={{ name: formData.name, email: formData.email, company: formData.company }}
        strategySessionId={strategyId}
      />
    </div>
  );
};

export default StrategySessionForm;
